import type { ShellUiConfig, RemoteNavConfig } from '@opengov/psp-contract';

/** Aggregated UI config across all mounted apps. */
export interface AggregatedShellUiConfig {
  remoteNav?: RemoteNavConfig;
  /** App id whose remoteNav is currently active (routed RemoteAppShell only). */
  remoteNavSourceAppId?: string;
}

type Listener = () => void;

/**
 * Observable store holding ShellUiConfig from all mounted apps.
 * Keyed by appId so each app's contributions are isolated.
 * Consumed by React components via useSyncExternalStore.
 *
 * Aggregation policy for `remoteNav`: only the **active routed** app's
 * contribution is exposed (set via `setActiveRoutedAppId` from RemoteAppShell).
 * Global apps may still call setUiContributions, but they never become active
 * and therefore never drive the shell-owned NavBar.
 */
export class ShellBridgeStore {
  private contributions = new Map<string, ShellUiConfig>();
  private listeners = new Set<Listener>();
  private snapshot: AggregatedShellUiConfig = Object.freeze({});
  private activeRoutedAppId: string | undefined;

  setContributions(appId: string, config: ShellUiConfig): void {
    this.contributions.set(appId, config);
    // Only rebuild and notify if this app is active or no app is active yet.
    // Non-active apps (global mounts) may contribute, but they don't drive the snapshot.
    if (appId === this.activeRoutedAppId || !this.activeRoutedAppId) {
      this.rebuildSnapshot();
      this.notify();
    }
  }

  clearContributions(appId: string): void {
    if (this.contributions.delete(appId)) {
      // Only rebuild and notify if the cleared app was active.
      // Non-active apps clearing their contributions don't affect the snapshot.
      if (appId === this.activeRoutedAppId) {
        this.rebuildSnapshot();
        this.notify();
      }
    }
  }

  /**
   * Mark which routed RemoteAppShell currently owns the main content area.
   * Only this app's `remoteNav` appears in the aggregated snapshot.
   */
  setActiveRoutedAppId(appId: string): void {
    if (this.activeRoutedAppId === appId) return;
    this.activeRoutedAppId = appId;
    this.rebuildSnapshot();
    this.notify();
  }

  /**
   * Clear the active routed app when a RemoteAppShell unmounts.
   * No-op if a different app has already become active (route transition).
   */
  clearActiveRoutedAppId(appId: string): void {
    if (this.activeRoutedAppId !== appId) return;
    this.activeRoutedAppId = undefined;
    this.rebuildSnapshot();
    this.notify();
  }

  getSnapshot = (): AggregatedShellUiConfig => this.snapshot;

  /** Subscribe for useSyncExternalStore compatibility */
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private rebuildSnapshot(): void {
    const activeId = this.activeRoutedAppId;
    const activeConfig = activeId ? this.contributions.get(activeId) : undefined;
    const remoteNav = activeConfig?.remoteNav;
    this.snapshot = Object.freeze({
      remoteNav,
      remoteNavSourceAppId: remoteNav ? activeId : undefined,
    });
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
